import axios from 'axios';
import { API_CONFIG } from '../config/api';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: API_CONFIG.headers,
  withCredentials: false,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// Fallback fetch method for CORS issues
const fetchAPI = async (endpoint, options = {}) => {
  const possibleURLs = [
    process.env.REACT_APP_API_URL,
    'http://localhost:8080',
    'http://localhost:3001',
    'http://localhost:5000',
    'http://localhost:8000'
  ].filter(Boolean);

  let lastError;

  for (const baseURL of possibleURLs) {
    const url = `${baseURL}${endpoint}`;

    console.log(`Trying domain API call to: ${url}`);

    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      mode: 'cors',
      ...options,
    };

    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      defaultOptions.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, defaultOptions);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Server error' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      console.log(`✅ Domain API success with: ${url}`);
      return await response.json();
    } catch (error) {
      console.log(`❌ Domain API failed with ${url}:`, error.message);
      lastError = error;
      continue;
    }
  }

  throw new Error(`Failed to connect to domain API. Tried: ${possibleURLs.join(', ')}. Last error: ${lastError?.message}`);
};

export const domainService = {
  // Get all domains
  getDomains: async () => {
    try {
      const endpoint = '/domains';

      let response;
      try {
        response = await api.get(endpoint);
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI(endpoint, {
          method: 'GET',
        });
      }

      return response;
    } catch (error) {
      console.error('Get domains error:', error);
      throw error;
    }
  },
};

export default domainService;
